import { useBrowseProductEntitlementApi } from '@aibees/shared/api';
import type {
  Entitlement,
  FeatureProduct,
  Plan,
} from '@aibees/shared/definition';
import usePriceAndPlanStore from '@aibees/shared/features/price-and-plan/store';
import { useMemo } from 'react';
import { sortEntitlements } from '../plan-detail/utils/entitlement';
import { usePlanList } from '../pricing-cards/hooks/use-plan-list';
import type {
  CellValue,
  FeatureRow,
  FeatureSection,
  SectionCoverage,
} from './types';

function sortProducts(products: FeatureProduct[]): FeatureProduct[] {
  return [...products].sort((a, b) => {
    const orderA = Number(a.metadata?.order) || 0;
    const orderB = Number(b.metadata?.order) || 0;
    return orderA - orderB;
  });
}

function resolveCellValue(
  name: string,
  productId: string,
  entitlements: Entitlement[]
): CellValue {
  const match = entitlements.find(
    (e) => e.name === name && e.products.includes(productId)
  );
  if (!match) return 'dash';
  return match.metadata.value || 'check';
}

function buildRow(
  name: string,
  products: FeatureProduct[],
  entitlements: Entitlement[]
): FeatureRow {
  const values: Record<string, CellValue> = {};
  for (const product of products) {
    values[product.id] = resolveCellValue(name, product.id, entitlements);
  }
  return { feature: name, values };
}

function buildSectionCoverage(
  uniqueNames: string[],
  sectionEntitlements: Entitlement[],
  products: FeatureProduct[]
): {
  headerValues: FeatureSection['headerValues'];
  headerCoverage: FeatureSection['headerCoverage'];
} {
  const headerValues: Record<string, CellValue> = {};
  const headerCoverage: Record<string, SectionCoverage> = {};
  const total = uniqueNames.length;

  for (const product of products) {
    const filled = uniqueNames.reduce((count, name) => {
      const hasFeature = sectionEntitlements.some(
        (e) => e.name === name && e.products.includes(product.id)
      );
      return hasFeature ? count + 1 : count;
    }, 0);

    headerCoverage[product.id] = { filled, total };
    headerValues[product.id] = filled > 0 ? 'check' : 'dash';
  }

  return { headerValues, headerCoverage };
}

function groupPlansByProductId(
  plans: Plan[] | undefined,
  freePlan: Plan | undefined
): Record<string, Plan[]> {
  const grouped: Record<string, Plan[]> = {};
  if (freePlan?.product?.id) {
    (grouped[freePlan.product.id] ??= []).push(freePlan);
  }
  if (plans) {
    for (const plan of plans) {
      const productId = plan.product?.id;
      if (productId) {
        (grouped[productId] ??= []).push(plan);
      }
    }
  }
  return grouped;
}

export function useCollapsedTableData() {
  const { data, isLoading } = useBrowseProductEntitlementApi();
  const yearly = usePriceAndPlanStore.useYearly();
  const {
    plans: subscriptionPlans,
    freePlan,
    isLoading: plansLoading,
  } = usePlanList(yearly);

  const plansByProduct = useMemo(
    () => groupPlansByProductId(subscriptionPlans, freePlan),
    [subscriptionPlans, freePlan]
  );

  return useMemo(() => {
    const features = data?.features ?? [];
    const products = sortProducts(data?.products ?? []);

    const sorted = sortEntitlements([...features]);

    const parentGroups: Record<string, Entitlement[]> = {};
    for (const ent of sorted) {
      const key = ent.metadata.parent || 'no-parent';
      (parentGroups[key] ??= []).push(ent);
    }

    const noParent = parentGroups['no-parent'] ?? [];
    const uniqueTopNames = Array.from(new Set(noParent.map((e) => e.name)));
    const topRows: FeatureRow[] = uniqueTopNames.map((name) =>
      buildRow(name, products, features)
    );

    const sections: FeatureSection[] = Object.entries(parentGroups)
      .filter(([parent]) => parent !== 'no-parent')
      .map(([parent, entList]) => {
        const uniqueNames = Array.from(new Set(entList.map((e) => e.name)));
        const { headerValues, headerCoverage } = buildSectionCoverage(
          uniqueNames,
          entList,
          products
        );
        return {
          title: parent,
          headerValues,
          headerCoverage,
          features: uniqueNames.map((name) =>
            buildRow(name, products, features)
          ),
        };
      });

    return {
      products,
      topRows,
      sections,
      isLoading: isLoading || plansLoading,
      plansByProduct,
      yearly,
    };
  }, [data, isLoading, plansLoading, plansByProduct, yearly]);
}
